/** Starting an operation and putting a ranger on the aircraft (04 §2.1, §3, §6). */

import { clamp } from '../sim/math';
import { Pcg32 } from '../sim/rng';
import type { Loadout, MissionParams } from '../sim/types';
import { closeOperation } from './aftermath/endings';
import type { Deployment } from './api';
import { formCompany } from './company';
import { archetype } from './content/archetypes';
import { hazardRules } from './content/career';
import { CAPS, FIXED_POD, STANDARD_ISSUE } from './content/economy';
import { intelFor } from './content/modifiers';
import { practiceParams, stampDogTeams } from './dogs';
import { cleanGear, startingStock, takeGear } from './gear';
import { priceLoadout, standardIssue } from './loadout';
import { openNodes, refreshNodes } from './map';
import { carryBonus, modsFor } from './mods';
import { generateOperation } from './opgen';
import { pruneFlights } from './records';
import { enforceSky } from './opgen/dress';
import {
  activeOperation, companyRanger, findNode, fullName, type OperationState, type ProfileState, snapshot,
} from './state';
import { nodeThreat, rewardMultiplier } from './threat';

/**
 * `difficulty` is the 0-3 slider. The contract has no hazard parameter, so a hazard level
 * rides in the tens: 21 = Hazard 2 on Standard. It is capped at what the profile has unlocked.
 */
export function startOperation(
  profile: ProfileState, difficulty: number, seed: number, cadet: boolean, veteranIds: string[],
): OperationState {
  if (profile.operation?.status === 'active') throw new Error('An operation is already under way.');
  const asked = Math.max(0, Math.floor(difficulty));
  const base = clamp(asked % 10, 0, 3);
  const hazard = clamp(Math.floor(asked / 10), 0, profile.hazardUnlocked);
  const rules = hazardRules(hazard);
  const generated = generateOperation(base, hazard, seed);
  const company = formCompany(profile, veteranIds, new Pcg32(seed >>> 0, 0x0903), rules);
  const op: OperationState = {
    seed: seed >>> 0, codename: generated.codename, difficulty: base, hazard, theatres: generated.theatres,
    nodes: generated.nodes, position: null, company, rp: rules.startRp, readiness: rules.readinessStart,
    keystoneFailures: 0, status: 'active', log: [`Operation ${generated.codename} begins.`], cadet,
    trainablePerks: [], deployed: null, rescueNodeId: null, stock: startingStock(profile.unlocks), intelHeld: [],
    revealedTheatres: [], forcedRolled: [], luckyUsed: [], cadetRestarts: [], inducted: 0, tagNodes: {},
  };
  refreshNodes(op);
  profile.operation = op;
  // The last operation's company is gone with it now. Anyone who was neither inducted, retired nor
  // killed is no longer anybody the profile knows, so his service file goes too (04 §5).
  pruneFlights(profile);
  return op;
}

/**
 * Withdrawal (04 §2.4): survivors are not inducted, captives are lost, veterans go back to the Hall. Allowed at
 * any time between missions - the owner's call on 2026-09-23; it used to wait for a theatre boundary. Never with
 * a ranger in the field: there is no abort (08 §2.8), and withdrawing is not a way to pull a man out of a mission.
 */
export function withdraw(profile: ProfileState): void {
  const op = activeOperation(profile);
  if (op.deployed) throw new Error('A ranger is in the field: the operation cannot be abandoned now.');
  op.log.push(`Operation ${op.codename}: the company withdraws.`);
  closeOperation(profile, op, 'withdrawn');
}

/** Validate, charge and commit. From here the ranger is Deployed: there is no un-deploy (04 §3). */
export function deployOperation(profile: ProfileState, nodeId: string, rangerId: string, loadout: Loadout): Deployment {
  const op = activeOperation(profile);
  const node = findNode(op, nodeId);
  if (op.deployed) {
    const again = op.deployed.nodeId === nodeId && op.deployed.rangerId === rangerId;
    if (op.cadet && again && !op.cadetRestarts.includes(node.theatre)) {
      op.cadetRestarts.push(node.theatre);
      op.log.push('Cadet option: the mission is restarted.');
      return op.deployed.deployment;
    }
    throw new Error('A ranger is already in the field: that mission must be reported first.');
  }
  if (!openNodes(op).some((n) => n.id === nodeId)) throw new Error('That node cannot be reached from here.');
  const params = node.params;
  if (!params || (node.kind !== 'mission' && node.kind !== 'rescue')) {
    throw new Error('That node is not a mission: visit it instead.');
  }
  const ranger = companyRanger(op, rangerId);
  if (ranger.status !== 'ready') throw new Error(`${fullName(ranger)} cannot deploy (${ranger.status}).`);

  // A free card from a Signals Intercept waits for a mission it can be used on. Spending a strip-map
  // card on a Radio Silence mission, or a fourth pod on a mission with no drop, is the card doing
  // nothing - and the card was still gone. It stays in hand for the next one instead.
  const usable = intelFor(params.archetype, params.modifiers, params.threat).map((card) => card.id);
  const carried = op.intelHeld.filter((id) => usable.includes(id));
  const intel = [...new Set([...params.intel, ...carried])];
  const modifiers = intel.includes('weather-window') ? params.modifiers.filter((m) => m !== 'storm') : params.modifiers;
  const maxPods = CAPS.pods - (modifiers.includes('two-pod-limit') ? 1 : 0)
    + (intel.includes('pathfinder-team') ? 1 : 0);
  const cost = priceLoadout(loadout, {
    carryCap: CAPS.carry + carryBonus(ranger), maxPods, standard: standardIssue(hazardRules(op.hazard)), free: false,
  });
  if (cost > op.rp) throw new Error(`That loadout costs ${cost} RP; the company has ${op.rp}.`);
  const gear = cleanGear(loadout.gear);
  takeGear(op, gear);

  Object.assign(params, { intel, modifiers, threat: nodeThreat(op, node) });
  enforceSky(params);
  stampDogTeams(params, op.difficulty);
  node.rewardMultiplier = rewardMultiplier(modifiers, node.variant);
  op.intelHeld = op.intelHeld.filter((id) => !usable.includes(id));
  op.rp -= cost;
  const flown: Loadout = { carried: snapshot(loadout.carried), pods: snapshot(loadout.pods) };
  if (gear) flown.gear = gear;
  const deployment: Deployment = {
    params: snapshot(params), rangerId, loadout: flown,
    mods: modsFor(ranger, params, !op.luckyUsed.includes(ranger.id)), mode: 'operation',
  };
  op.deployed = { nodeId, rangerId, cost, deployment };
  op.log.push(`${fullName(ranger)} deploys: ${archetype(params.archetype).name}${cost > 0 ? ` (-${cost} RP)` : ''}.`);
  return deployment;
}

/**
 * Practice: standard issue and the C64's three fixed pods; nothing is recorded (04 §1). Its dog teams follow
 * the threat rating at Standard (ROUND8), whatever the screen sent.
 */
export function practiceDeployment(params: MissionParams): Deployment {
  return {
    params: snapshot(practiceParams(params)), rangerId: 'practice',
    loadout: { carried: { ...STANDARD_ISSUE }, pods: [{ ...FIXED_POD }, { ...FIXED_POD }, { ...FIXED_POD }] },
    mods: {}, mode: 'practice',
  };
}
